import { useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogIn, User as UserIcon, Lock, Loader2, KeyRound } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
  const { login } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "setup"
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username.trim(), password);
      toast.success("Welcome back!");
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.detail || "Login failed";
      if (status === 428) {
        // First-time setup required — auto-switch UI
        setMode("setup");
        setError("");
        toast.info("First-time setup: create your password to continue.");
      } else {
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/first-time-setup`, {
        username: username.trim(),
        new_password: newPassword,
      });
      // Now log in with the freshly set password so AuthContext hydrates cleanly.
      await login(username.trim(), newPassword);
      toast.success("Password set — signed in!");
    } catch (err) {
      const msg = err.response?.data?.detail || "Setup failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const isSetup = mode === "setup";

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-sm bg-[#0F172A] text-white mb-4">
            {isSetup ? <KeyRound className="w-8 h-8" /> : <LogIn className="w-8 h-8" />}
          </div>
          <h1
            className="text-3xl font-semibold tracking-tight text-[#09090B]"
            style={{ fontFamily: "Manrope" }}
          >
            {isSetup ? "Set Your Password" : "Site Job Manager"}
          </h1>
          <p className="text-sm text-[#71717A] mt-1">
            {isSetup
              ? "Choose a password to activate your account. You'll use this to sign in from now on."
              : "Sign in to manage work orders, engineers, and billing"}
          </p>
        </div>

        <div className="bg-white rounded-sm border border-[#E4E4E7] p-6">
          <form onSubmit={isSetup ? handleSetup : handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-xs tracking-[0.15em] uppercase font-bold text-[#71717A]">
                Username
              </Label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#71717A] w-4 h-4" />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  required
                  autoComplete="username"
                  autoFocus={!isSetup}
                  readOnly={isSetup}
                  className={`pl-10 border-[#E4E4E7] rounded-sm h-11 ${isSetup ? "bg-[#F4F4F5]" : ""}`}
                  data-testid="login-username-input"
                />
              </div>
            </div>

            {!isSetup && (
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs tracking-[0.15em] uppercase font-bold text-[#71717A]">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#71717A] w-4 h-4" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                    className="pl-10 border-[#E4E4E7] rounded-sm h-11"
                    data-testid="login-password-input"
                  />
                </div>
              </div>
            )}

            {isSetup && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-xs tracking-[0.15em] uppercase font-bold text-[#71717A]">
                    New Password (min 8 chars)
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#71717A] w-4 h-4" />
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Create a password"
                      required
                      minLength={8}
                      autoFocus
                      autoComplete="new-password"
                      className="pl-10 border-[#E4E4E7] rounded-sm h-11"
                      data-testid="setup-new-password-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-xs tracking-[0.15em] uppercase font-bold text-[#71717A]">
                    Confirm Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#71717A] w-4 h-4" />
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repeat the password"
                      required
                      minLength={8}
                      autoComplete="new-password"
                      className="pl-10 border-[#E4E4E7] rounded-sm h-11"
                      data-testid="setup-confirm-password-input"
                    />
                  </div>
                </div>
              </>
            )}

            {error && (
              <div
                className="text-sm text-[#DC2626] bg-[#FEE2E2] border border-[#FECACA] rounded-sm px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-sm h-11 transition-colors duration-200"
              data-testid={isSetup ? "setup-submit-btn" : "login-submit-btn"}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isSetup ? "Creating account..." : "Signing in..."}
                </>
              ) : (
                <>
                  {isSetup ? <KeyRound className="w-4 h-4 mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
                  {isSetup ? "Set Password & Continue" : "Sign In"}
                </>
              )}
            </Button>

            {!isSetup && (
              <button
                type="button"
                onClick={() => {
                  setMode("setup");
                  setError("");
                }}
                className="w-full text-xs text-[#71717A] hover:text-[#0F172A] pt-1 underline underline-offset-4 transition-colors"
                data-testid="switch-to-setup-btn"
              >
                First time signing in? Set your password →
              </button>
            )}
            {isSetup && (
              <button
                type="button"
                onClick={() => {
                  setMode("login");
                  setError("");
                  setNewPassword("");
                  setConfirmPassword("");
                }}
                className="w-full text-xs text-[#71717A] hover:text-[#0F172A] pt-1 underline underline-offset-4 transition-colors"
                data-testid="switch-to-login-btn"
              >
                ← Back to sign in
              </button>
            )}
          </form>
        </div>

        <p className="text-center text-xs text-[#71717A] mt-6">
          Access is invite-only. Contact your administrator to request an account.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
